import { useState } from "react";
import { ShoppingCart, X, Plus, Minus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCart } from "@/contexts/CartContext";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

const CartButton = () => {
  const { items, totalItems, totalPrice, removeFromCart, updateQuantity } = useCart();
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative text-foreground hover:text-accent"
          aria-label={`Shopping cart with ${totalItems} items`}
        >
          <ShoppingCart className="h-5 w-5" aria-hidden="true" />
          {totalItems > 0 && (
            <span 
              className="absolute -top-1 -right-1 bg-accent text-black text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
              aria-hidden="true"
            >
              {totalItems}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="bg-black border-white/10 text-white w-full sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Your Cart ({totalItems})
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 flex flex-col h-[calc(100vh-200px)]">
          {items.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <ShoppingCart className="h-16 w-16 mb-4 opacity-30" />
              <p>Your cart is empty</p>
              <Button
                variant="outline"
                className="mt-4 border-accent text-accent hover:bg-accent hover:text-black"
                onClick={() => setOpen(false)}
                asChild
              >
                <Link to="/store">Browse Store</Link>
              </Button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto space-y-4 pr-2">
                {items.map((item, index) => (
                  <div
                    key={`${item.id}-${index}`}
                    className="flex gap-3 p-3 bg-white/5 rounded-lg border border-white/10"
                  >
                    {item.image && (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm truncate">{item.name}</h4>
                      {item.options?.color && (
                        <p className="text-xs text-gray-400">Color: {item.options.color}</p>
                      )}
                      {item.options?.capacity && (
                        <p className="text-xs text-gray-400">Capacity: {item.options.capacity}</p>
                      )}
                      <p className="text-accent font-bold mt-1">
                        {item.price.toLocaleString()} {item.currency}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-11 w-11 min-h-[44px] min-w-[44px] text-muted-foreground hover:text-destructive"
                        onClick={() => removeFromCart(item.id)}
                        aria-label={`Remove ${item.name} from cart`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 min-h-[44px] min-w-[44px]"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          aria-label={`Decrease quantity of ${item.name}`}
                        >
                          <Minus className="h-3 w-3" aria-hidden="true" />
                        </Button>
                        <span className="w-8 text-center text-sm" aria-label={`Quantity: ${item.quantity}`}>{item.quantity}</span>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-11 w-11 min-h-[44px] min-w-[44px]"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          aria-label={`Increase quantity of ${item.name}`}
                        >
                          <Plus className="h-3 w-3" aria-hidden="true" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-white/10 pt-4 mt-4 space-y-4">
                <div className="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span className="text-accent">{totalPrice.toLocaleString()} EGP</span>
                </div>
                <Button
                  className="w-full bg-accent text-black hover:bg-accent/90 font-bold uppercase"
                  onClick={() => setOpen(false)}
                  asChild
                >
                  <Link to="/store">Continue Shopping</Link>
                </Button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartButton;
